import { useMemo, useSyncExternalStore } from "react";

/** Same-tab auth changes are invisible to the storage event; login/logout
 *  call sites dispatch this event so open v2 surfaces re-render. The v1
 *  login/logout wiring that calls notifyAuthChanged() lands with
 *  HTPTV2-2; whoever wires it in must also extend V1_ALLOWLIST in
 *  src/v2/__tests__/boundaries.test.ts to cover the new call site. */
export const AUTH_CHANGED_EVENT = "v2:auth-changed";

export type AuthUser = {
  userId: string | number;
  firstName: string;
  lastName: string;
  email: string;
  profileImage?: string;
  createdAt?: string;
  isEmailVerified: boolean;
};

/** Coerce the shapes the backend may use for a boolean flag. */
function parseBool(value: unknown): boolean {
  return (
    value === true ||
    value === 1 ||
    String(value).toLowerCase() === "true" ||
    String(value) === "1"
  );
}

export type AuthState = {
  isAuthenticated: boolean;
  user: AuthUser | null;
};

export type AuthChangedDetail = {
  /** True when this is a profile-field sync (name/photo), not a real
   *  login/logout transition - lets AuthChannelSync skip its channel
   *  refetch, which is unrelated to a display-name or avatar change. */
  profileOnly?: boolean;
};

export function notifyAuthChanged(detail?: AuthChangedDetail): void {
  window.dispatchEvent(new CustomEvent(AUTH_CHANGED_EVENT, { detail }));
}

function subscribe(onStoreChange: () => void): () => void {
  window.addEventListener("storage", onStoreChange);
  window.addEventListener(AUTH_CHANGED_EVENT, onStoreChange);
  return () => {
    window.removeEventListener("storage", onStoreChange);
    window.removeEventListener(AUTH_CHANGED_EVENT, onStoreChange);
  };
}

function getSnapshot(): string {
  return localStorage.getItem("auth") ?? "";
}

function parseAuth(raw: string): Record<string, unknown> {
  if (!raw) return {};
  try {
    return JSON.parse(raw) as Record<string, unknown>;
  } catch {
    return {};
  }
}

export function useAuthState(): AuthState {
  const raw = useSyncExternalStore(subscribe, getSnapshot);
  return useMemo(() => {
    const auth = parseAuth(raw);
    if (!auth.AccessToken) return { isAuthenticated: false, user: null };
    return {
      isAuthenticated: true,
      user: {
        userId: (auth.UserID as string | number) ?? "",
        firstName: (auth.FirstName as string) ?? "",
        lastName: (auth.LastName as string) ?? "",
        email: (auth.Email as string) ?? "",
        profileImage: auth.ProfileImage as string | undefined,
        createdAt: (auth.createdAt as string) ?? "",
        // Use the real flag when the backend provides it; otherwise, since login
        // is blocked for unverified emails, an authenticated user is verified.
        isEmailVerified: (() => {
          const flag =
            auth.IsEmailVerified ?? auth.is_email_verified ?? auth.isEmailVerified;
          return flag === undefined ? true : parseBool(flag);
        })(),
      },
    };
  }, [raw]);
}
